import { useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ViewInArRoundedIcon from "@mui/icons-material/ViewInArRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import ModelViewerOverlay from "./ModelViewerOverlay";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  DeleteRelated3DLinkCall,
  GetRelated3DSuggestionsCall,
  UpsertRelated3DLinkCall,
} from "../../../redux/api/related3d.api";
import { GetTitlesCallNoStore } from "../../../redux/api/title.api";

const TARGET_TYPE_BY_SOURCE = {
  his: "three_d_model",
  three_d_model: "his",
  emb: "emb_3d_model",
  emb_3d_model: "emb",
};

const MODEL_TYPES = new Set(["three_d_model", "emb_3d_model"]);

const PANEL_TITLE_BY_SOURCE = {
  his: "Related 3D Models",
  emb: "Related 3D Models",
  three_d_model: "Related Histology Slides",
  emb_3d_model: "Related Embryology Slides",
};

function titleFromRow(row) {
  return row?.title || row?.counterpart_title?.localized_title || row?.counterpart_title_id || "Untitled";
}

function getLocalizedTitle(entity) {
  return entity?.titleLocalizations?.find((x) => x.field === "title")?.value || entity?.id || "Untitled";
}

function getEntityImage(entity) {
  if (entity?.image) return entity.image;
  const firstSlide = Array.isArray(entity?.slides) ? entity.slides[0] : null;
  const firstFile = Array.isArray(firstSlide?.Files) ? firstSlide.Files[0] : null;
  return firstFile?.url || "";
}

export default function RelatedModelsPanel({
  sourceTitleId,
  sourceType,
  isAdmin,
  lang = "en",
  refreshKey = 0,
  compact = false,
  minWidth = 280,
  maxWidth = 340,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [candidateId, setCandidateId] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [candidates, setCandidates] = useState([]);
  const [viewerIndex, setViewerIndex] = useState(null);
  const [opening, setOpening] = useState(null);
  const navigate = useNavigate();

  const modelRows = useMemo(() => rows.filter((r) => MODEL_TYPES.has(r.related_type)), [rows]);

  const openRow = async (row) => {
    if (MODEL_TYPES.has(row.related_type)) {
      setViewerIndex(modelRows.findIndex((r) => r.id === row.id));
      return;
    }
    // Related histology / embryology slide: go to its viewer page.
    let slideId = row.first_slide_id;
    if (!slideId) {
      setOpening(row.id);
      const list = await GetTitlesCallNoStore(lang, row.related_type);
      const target = String(row.counterpart_title_id);
      for (const title of Array.isArray(list) ? list : []) {
        const sub =
          String(title.id) === target
            ? title.subtitles?.[0]
            : (title.subtitles || []).find((x) => String(x.id) === target);
        if (sub?.slides?.[0]?.id) {
          slideId = sub.slides[0].id;
          break;
        }
      }
      setOpening(null);
    }
    if (slideId) navigate(`/showTitle/${slideId}/${lang}`);
  };

  const targetType = useMemo(
    () => TARGET_TYPE_BY_SOURCE[sourceType] || null,
    [sourceType]
  );
  const panelTitle = PANEL_TITLE_BY_SOURCE[sourceType] || "Related Items";

  const loadRows = async () => {
    if (!sourceTitleId || !sourceType || !targetType) {
      setRows([]);
      return;
    }
    setLoading(true);
    const res = await GetRelated3DSuggestionsCall({
      source_title_id: sourceTitleId,
      lang,
      limit: 30,
    });
    if (res?.status === 200 && Array.isArray(res.data)) {
      setRows(res.data);
    } else {
      setRows([]);
    }
    setLoading(false);
  };

  const loadCandidates = async () => {
    if (!targetType) return;
    const list = await GetTitlesCallNoStore(lang, targetType);
    const flattened = [];
    (Array.isArray(list) ? list : []).forEach((title) => {
      const subtitles = Array.isArray(title?.subtitles) ? title.subtitles : [];
      if (subtitles.length > 0) {
        subtitles.forEach((sub) => {
          flattened.push({
            id: sub.id,
            title: getLocalizedTitle(sub),
            image: getEntityImage(sub) || getEntityImage(title),
          });
        });
      } else {
        flattened.push({
          id: title.id,
          title: getLocalizedTitle(title),
          image: getEntityImage(title),
        });
      }
    });
    setCandidates(flattened);
  };

  const selectedCandidate = useMemo(
    () => candidates.find((c) => String(c.id) === String(candidateId)) || null,
    [candidates, candidateId]
  );

  useEffect(() => {
    loadRows();
  }, [sourceTitleId, sourceType, targetType, lang, refreshKey]);

  useEffect(() => {
    if (isAdmin && showAdd) loadCandidates();
  }, [isAdmin, showAdd, targetType]);

  const onAdd = async () => {
    if (!sourceTitleId || !candidateId) return;
    const res = await UpsertRelated3DLinkCall({
      source_title_id: sourceTitleId,
      related_title_id: candidateId,
      sort_order: Number(sortOrder || 0),
      is_active: true,
    });
    if (res?.status === 200) {
      setCandidateId("");
      setSortOrder(0);
      setShowAdd(false);
      loadRows();
    }
  };

  const onRemove = async (id) => {
    const res = await DeleteRelated3DLinkCall(id);
    if (res?.status === 200) {
      loadRows();
    }
  };

  if (!sourceTitleId || !sourceType || !targetType) {
    return null;
  }

  return (
    <Card style={{ marginTop: compact ? 0 : 16, minWidth, maxWidth, width: "100%" }}>
      <MDBox p={2}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant={compact ? "button" : "h6"} fontWeight={compact ? "bold" : "regular"}>
            {panelTitle}
          </MDTypography>
          {isAdmin && (
            <Tooltip title={showAdd ? "Close" : "Add relation"}>
              <IconButton color="info" onClick={() => setShowAdd((v) => !v)}>
                {showAdd ? <CloseIcon /> : <AddIcon />}
              </IconButton>
            </Tooltip>
          )}
        </MDBox>
        <Divider sx={{ my: 1.5 }} />

        {isAdmin && showAdd && (
          <MDBox display="flex" gap={1} flexWrap="wrap" mb={2} alignItems="flex-start">
            <Autocomplete
              size="small"
              options={candidates}
              value={selectedCandidate}
              onChange={(_, next) => setCandidateId(next?.id || "")}
              getOptionLabel={(option) => option?.title || ""}
              isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
              renderOption={(props, option) => (
                <li {...props} key={option.id} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {option.image ? (
                    <img
                      src={option.image}
                      alt=""
                      style={{
                        width: 26,
                        height: 26,
                        objectFit: "cover",
                        borderRadius: 4,
                        border: "1px solid rgba(0,0,0,0.12)",
                      }}
                    />
                  ) : (
                    <MDBox
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: 1,
                        background: "rgba(0,0,0,0.08)",
                      }}
                    />
                  )}
                  <span>{option.title}</span>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Related subtitle"
                  placeholder="Search and select subtitle"
                />
              )}
              sx={{
                minWidth: compact ? 220 : 320,
                flex: 1,
                "& .MuiInputBase-root": { minHeight: compact ? 42 : 44 },
              }}
            />
            <TextField
              size="small"
              label="Sort"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              sx={{
                width: compact ? 80 : 100,
                "& .MuiInputBase-root": { minHeight: compact ? 42 : 44 },
              }}
            />
            <MDButton variant="gradient" color="info" onClick={onAdd}>
              Add
            </MDButton>
          </MDBox>
        )}

        {loading ? (
          <MDTypography variant="caption" color="text">Loading...</MDTypography>
        ) : rows.length === 0 ? (
          <MDTypography variant="caption" color="text">
            No related items yet.
          </MDTypography>
        ) : (
          <MDBox
            display="flex"
            flexDirection="column"
            gap={1}
            sx={{ maxHeight: compact ? 520 : "none", overflowY: compact ? "auto" : "visible" }}
          >
            {rows.map((row) => {
              const isModel = MODEL_TYPES.has(row.related_type);
              return (
                <MDBox
                  key={row.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openRow(row)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openRow(row);
                    }
                  }}
                  display="flex"
                  alignItems="center"
                  sx={{
                    gap: 1.25,
                    p: 1,
                    borderRadius: 2.5,
                    cursor: "pointer",
                    border: "1px solid rgba(148,163,184,0.25)",
                    transition: "border-color 140ms ease, background 140ms ease, transform 140ms ease",
                    opacity: opening === row.id ? 0.6 : 1,
                    "&:hover, &:focus-visible": {
                      borderColor: alpha("#0061FE", 0.45),
                      bgcolor: alpha("#0061FE", 0.04),
                      outline: "none",
                    },
                    "&:hover .related-thumb-badge": { transform: "scale(1.08)" },
                    "&:active": { transform: "scale(0.99)" },
                  }}
                >
                  <MDBox
                    sx={{
                      position: "relative",
                      width: compact ? 52 : 60,
                      height: compact ? 52 : 60,
                      borderRadius: 2,
                      overflow: "hidden",
                      flexShrink: 0,
                      bgcolor: "#0f172a",
                      backgroundImage: row?.image ? `url(${row.image})` : "radial-gradient(circle at 50% 40%, #334155, #0f172a)",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  >
                    {isModel ? (
                      <MDBox
                        className="related-thumb-badge"
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "grid",
                          placeItems: "center",
                          color: "#fff",
                          bgcolor: row?.image ? "rgba(2,6,23,0.35)" : "transparent",
                          transition: "transform 160ms ease",
                        }}
                      >
                        <ViewInArRoundedIcon sx={{ fontSize: 22 }} />
                      </MDBox>
                    ) : null}
                  </MDBox>
                  <MDBox sx={{ flex: 1, minWidth: 0 }}>
                    <MDTypography
                      variant={compact ? "caption" : "button"}
                      fontWeight="bold"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        lineHeight: 1.35,
                        wordBreak: "break-word",
                      }}
                    >
                      {titleFromRow(row)}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      display="block"
                      sx={{ mt: 0.25, lineHeight: 1.3, color: "#0061FE", fontWeight: 600 }}
                    >
                      {isModel ? "View in 3D" : "Open slide"}
                      {isAdmin ? (
                        <MDTypography component="span" variant="caption" color="text" sx={{ fontWeight: 400 }}>
                          {" "}· sort {row.sort_order}
                        </MDTypography>
                      ) : null}
                    </MDTypography>
                  </MDBox>
                  {isAdmin ? (
                    <Tooltip title="Remove relation">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemove(row.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <ChevronRightRoundedIcon sx={{ color: "text.secondary", flexShrink: 0 }} />
                  )}
                </MDBox>
              );
            })}
          </MDBox>
        )}
      </MDBox>
      <ModelViewerOverlay
        rows={modelRows}
        index={viewerIndex}
        onIndexChange={setViewerIndex}
        onClose={() => setViewerIndex(null)}
        lang={lang}
      />
    </Card>
  );
}

